import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { 
  Building2, Plus, Calendar, Users, Key, 
  Trash2, Edit, Copy, ChevronRight
} from "lucide-react";
import { toast } from "sonner";

const AdminPartners = () => {
  const { token } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePartner, setShowCreatePartner] = useState(false);
  const [showCreateCode, setShowCreateCode] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerCodes, setPartnerCodes] = useState({});
  const [editingPartner, setEditingPartner] = useState(null);
  
  // Form states
  const [newPartner, setNewPartner] = useState({
    organization_name: "",
    license_count: 100,
    expiration_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    admin_name: "",
    admin_email: "",
    admin_password: ""
  });
  
  const [newCode, setNewCode] = useState({
    max_uses: 50,
    expiration_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    code: ""
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await fetch(`${API}/admin/partners`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setPartners(data.partners);
      }
    } catch (error) {
      console.error("Fetch partners error:", error);
      toast.error("Failed to load partners");
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerCodes = async (partnerId) => {
    try {
      const response = await fetch(`${API}/admin/partners/${partnerId}/codes`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setPartnerCodes(prev => ({ ...prev, [partnerId]: data.codes }));
      }
    } catch (error) {
      console.error("Fetch codes error:", error);
    }
  };

  const savePartnerEdit = async () => {
    if (!editingPartner || !selectedPartner) return;
    try {
      const res = await fetch(`${API}/admin/partners/${selectedPartner.partner_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(editingPartner)
      });
      if (res.ok) {
        toast.success("Partner updated!");
        setEditingPartner(null);
        await fetchPartners();
        setSelectedPartner(prev => ({ ...prev, ...editingPartner }));
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update");
      }
    } catch { toast.error("Error updating partner"); }
  };


  const createPartner = async () => {
    if (!newPartner.organization_name) {
      toast.error("Organization name is required");
      return;
    }

    try {
      const response = await fetch(`${API}/admin/partners`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify(newPartner),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.admin_created && data.admin_credentials) {
          toast.success(`Partner created! Admin login: ${data.admin_credentials.email} / ${data.admin_credentials.temporary_password}`);
        } else {
          toast.success("Partner created");
        }
        setShowCreatePartner(false);
        setNewPartner({
          organization_name: "",
          license_count: 100,
          expiration_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
          admin_name: "",
          admin_email: "",
          admin_password: ""
        });
        fetchPartners();
      } else {
        toast.error(data.detail || "Failed to create partner");
      }
    } catch (error) {
      toast.error("Failed to create partner");
    }
  };

  const createCode = async (partnerId) => {
    try {
      const response = await fetch(`${API}/admin/partners/${partnerId}/codes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({
          partner_id: partnerId,
          ...newCode
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Code created: ${data.code}`);
        setShowCreateCode(null);
        setNewCode({
          max_uses: 50,
          expiration_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
          code: ""
        });
        fetchPartnerCodes(partnerId);
      } else {
        toast.error("Failed to create code");
      }
    } catch (error) {
      toast.error("Failed to create code");
    }
  };

  const deletePartner = async (partnerId) => {
    if (!confirm("Delete this partner? This will also remove all codes and deactivate partner users.")) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/admin/partners/${partnerId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        toast.success("Partner deleted");
        fetchPartners();
        setSelectedPartner(null);
      } else {
        toast.error("Failed to delete partner");
      }
    } catch (error) {
      toast.error("Failed to delete partner");
    }
  };

  const deleteCode = async (code, partnerId) => {
    if (!confirm("Delete this code?")) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/admin/codes/${code}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        toast.success("Code deleted");
        fetchPartnerCodes(partnerId);
      } else {
        toast.error("Failed to delete code");
      }
    } catch (error) {
      toast.error("Failed to delete code");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied to clipboard");
  };

  const selectPartner = (partner) => {
    setSelectedPartner(partner);
    if (!partnerCodes[partner.partner_id]) {
      fetchPartnerCodes(partner.partner_id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold text-secondary">
          Partner Organizations
        </h2>
        <Button
          onClick={() => setShowCreatePartner(true)}
          className="rounded-full bg-primary text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Partner
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Partners List */}
        <div className="space-y-4">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : partners.length === 0 ? (
            <Card className="p-8 text-center border border-orange-100">
              <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No partners yet</p>
              <Button
                onClick={() => setShowCreatePartner(true)}
                variant="outline"
                className="mt-4 rounded-full"
              >
                Create First Partner
              </Button>
            </Card>
          ) : (
            partners.map((partner) => (
              <Card
                key={partner.partner_id}
                onClick={() => selectPartner(partner)}
                className={`p-4 border cursor-pointer transition-all ${
                  selectedPartner?.partner_id === partner.partner_id
                    ? "border-primary bg-primary/5"
                    : "border-orange-100 hover:border-primary/50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-secondary">{partner.organization_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {partner.user_count || 0} users • {partner.code_count || 0} codes
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Partner Details */}
        {selectedPartner && (
          <Card className="p-6 border border-orange-100 h-fit">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-lg font-bold text-secondary">
                {selectedPartner.organization_name}
              </h3>
              <div className="flex gap-1">
                {editingPartner ? (
                  <>
                    <Button size="sm" onClick={savePartnerEdit} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl text-xs h-8">
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingPartner(null)} className="rounded-xl text-xs h-8">
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => setEditingPartner({
                    license_count: selectedPartner.license_count,
                    expiration_date: selectedPartner.expiration_date?.split("T")[0] || ""
                  })} className="text-blue-600 hover:bg-blue-50">
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deletePartner(selectedPartner.partner_id)}
                  className="text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-orange-50/50 rounded-xl p-3">
                <p className="text-sm text-muted-foreground">License Count</p>
                {editingPartner ? (
                  <Input type="number" value={editingPartner.license_count}
                    onChange={(e) => setEditingPartner(prev => ({...prev, license_count: parseInt(e.target.value)}))}
                    className="mt-1 h-8 rounded-lg" data-testid="edit-license-count" />
                ) : (
                  <p className="text-xl font-bold text-secondary">{selectedPartner.license_count}</p>
                )}
              </div>
              <div className="bg-orange-50/50 rounded-xl p-3">
                <p className="text-sm text-muted-foreground">Expires</p>
                {editingPartner ? (
                  <Input type="date" value={editingPartner.expiration_date}
                    onChange={(e) => setEditingPartner(prev => ({...prev, expiration_date: e.target.value}))}
                    className="mt-1 h-8 rounded-lg" data-testid="edit-expiration" />
                ) : (
                  <p className={`text-xl font-bold ${new Date(selectedPartner.expiration_date) < new Date() ? "text-red-500" : "text-secondary"}`}>
                    {new Date(selectedPartner.expiration_date).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>

            {/* Partner Codes */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="font-medium text-secondary">Access Codes</p>
                <Button
                  size="sm"
                  onClick={() => setShowCreateCode(selectedPartner.partner_id)}
                  className="rounded-full bg-primary/10 text-primary hover:bg-primary/20"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New Code
                </Button>
              </div>

              {partnerCodes[selectedPartner.partner_id]?.length > 0 ? (
                <div className="space-y-2">
                  {partnerCodes[selectedPartner.partner_id].map((code) => (
                    <div
                      key={code.code}
                      className="flex items-center justify-between p-3 bg-orange-50/50 rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        <Key className="w-4 h-4 text-primary" />
                        <div>
                          <p className="font-mono font-bold text-secondary">{code.code}</p>
                          <p className="text-xs text-muted-foreground">
                            {code.current_uses}/{code.max_uses} uses • Expires {new Date(code.expiration_date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyCode(code.code)}
                          className="rounded-full"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteCode(code.code, selectedPartner.partner_id)}
                          className="rounded-full text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No codes yet
                </p>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Create Partner Dialog */}
      <Dialog open={showCreatePartner} onOpenChange={setShowCreatePartner}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Partner Organization</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-secondary">Organization Name</label>
              <Input
                value={newPartner.organization_name}
                onChange={(e) => setNewPartner(prev => ({ ...prev, organization_name: e.target.value }))}
                placeholder="e.g., City Health Clinic"
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-secondary">License Count</label>
              <Input
                type="number"
                value={newPartner.license_count}
                onChange={(e) => setNewPartner(prev => ({ ...prev, license_count: parseInt(e.target.value) }))}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-secondary">Expiration Date</label>
              <Input
                type="date"
                value={newPartner.expiration_date}
                onChange={(e) => setNewPartner(prev => ({ ...prev, expiration_date: e.target.value }))}
                className="rounded-xl mt-1"
              />
            </div>
            
            {/* Admin Credentials Section */}
            <div className="border-t pt-4 mt-4">
              <p className="text-sm font-medium text-secondary mb-3">Admin Account (Optional)</p>
              <p className="text-xs text-muted-foreground mb-3">Create login credentials for the partner admin</p>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-secondary">Admin Name</label>
                  <Input
                    value={newPartner.admin_name}
                    onChange={(e) => setNewPartner(prev => ({ ...prev, admin_name: e.target.value }))}
                    placeholder="e.g., Dr. John Smith"
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary">Admin Email</label>
                  <Input
                    type="email"
                    value={newPartner.admin_email}
                    onChange={(e) => setNewPartner(prev => ({ ...prev, admin_email: e.target.value }))}
                    placeholder="admin@partner.com"
                    className="rounded-xl mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-secondary">Temporary Password</label>
                  <Input
                    type="text"
                    value={newPartner.admin_password}
                    onChange={(e) => setNewPartner(prev => ({ ...prev, admin_password: e.target.value }))}
                    placeholder="Set initial password"
                    className="rounded-xl mt-1"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePartner(false)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={createPartner} className="rounded-full bg-primary text-white">
              Create Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Code Dialog */}
      <Dialog open={!!showCreateCode} onOpenChange={() => setShowCreateCode(null)}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Generate Partner Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-secondary">Code (optional)</label>
              <Input
                value={newCode.code}
                onChange={(e) => setNewCode(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                placeholder="Leave empty to auto-generate"
                className="rounded-xl mt-1 font-mono"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-secondary">Max Uses</label>
              <Input
                type="number"
                value={newCode.max_uses}
                onChange={(e) => setNewCode(prev => ({ ...prev, max_uses: parseInt(e.target.value) }))}
                className="rounded-xl mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-secondary">Expiration Date</label>
              <Input
                type="date"
                value={newCode.expiration_date}
                onChange={(e) => setNewCode(prev => ({ ...prev, expiration_date: e.target.value }))}
                className="rounded-xl mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateCode(null)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={() => createCode(showCreateCode)} className="rounded-full bg-primary text-white">
              Generate Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPartners;
